import SACFrontendService from "@/services/SACFrontendService";
import { LectureService } from "@/services/CoachingServices";
import { mapGetters, mapActions } from "vuex";

export default {
  name: "LectureRenderless",

  props: {
    lectureId: {
      type: String,
      required: true,
    },
  },

  data() {
    return {
      isLoading: false,
      lecture: null,
    };
  },

  computed: {
    canStartLecture() {
      return this.lecture && this.lecture.status === "Created";
    },

    canCompleteLecture() {
      return this.lecture && this.lecture.status === "Started";
    },

    lectureStatus() {
      return this.lecture ? this.lecture.status : null;
    },
    ...mapGetters({
      currentClass: "getCurrentClass",
    }),

    classIdentifier() {
      return `${this.currentClass?.year}-${this.currentClass?.term}-${this.currentClass?.classId}`;
    },
  },

  watch: {
    classIdentifier: {
      handler() {
        if (!this.lecture?.id) return;
        this.loadClassDetails();
      },
      immediate: true,
    },

    lecture: {
      handler() {
        if (!this.lecture?.id) return;
        this.loadClassDetails();
      },
      immediate: true,
    },
  },

  methods: {
    ...mapActions({
      updateCurrentClass: "updateCurrentClass",
      updateCurrentLecture: "updateCurrentLecture",
    }),

    async loadClassDetails() {
      if (!this.currentClass || !this.currentClass.classId) {
        return;
      }

      try {
        const classDetails = await SACFrontendService.getClassDetails(
          this.currentClass.year,
          this.currentClass.term,
          this.currentClass.classId,
          this.lecture.id
        );

        this.updateCurrentClass({
          ...this.currentClass,
          ...classDetails,
        });
      } catch (error) {
        console.error(error);
        this.$buefy.toast.open({
          message: "Failed to load class details",
          type: "is-danger",
        });
      }
    },

    async loadLecture() {
      this.isLoading = true;
      try {
        this.lecture = await LectureService.getLecture(this.lectureId);
        this.updateCurrentLecture(this.lecture);
      } catch (error) {
        console.error("Error loading lecture:", error);
        this.$buefy.toast.open({
          message: "Failed to load lecture",
          type: "is-danger",
        });
      } finally {
        this.isLoading = false;
      }
    },

    async startLecture() {
      if (!this.canStartLecture) return;

      try {
        const teacherId = SACFrontendService.teacherInfo()?.teacherId;
        if (!teacherId) {
          throw new Error("Teacher ID not found");
        }

        const updatedLecture = await LectureService.startLecture(
          this.lectureId,
          teacherId
        );
        this.lecture = updatedLecture;

        this.$buefy.toast.open({
          message: "Lecture started",
          type: "is-success",
        });
      } catch (error) {
        console.error("Error starting lecture:", error);
        this.$buefy.toast.open({
          message: "Failed to start lecture",
          type: "is-danger",
        });
      }
    },

    async completeLecture() {
      if (!this.canCompleteLecture) return;

      try {
        const updatedLecture = await LectureService.completeLecture(
          this.lectureId
        );
        this.lecture = updatedLecture;

        this.$buefy.toast.open({
          message: "Lecture completed",
          type: "is-success",
        });
      } catch (error) {
        console.error("Error completing lecture:", error);
        this.$buefy.toast.open({
          message: "Failed to complete lecture",
          type: "is-danger",
        });
      }
    },
  },

  async created() {
    await this.loadLecture();
  },
};
